import pluralize from "pluralize";

type DurationUnit = "minute" | "hour" | "day";

interface ConvertUnitOptions {
  /**
   * If true, skip automatic unit conversion and keep the original unit
   * (e.g., "24 hours" instead of "1 day")
   * Default: false
   */
  keepOriginalUnit?: boolean;

  /**
   * If true, convert to larger units even when not evenly divisible
   * Default: false
   */
  truncatePrecision?: boolean;
}

export type FormatDurationLabelOptions = ConvertUnitOptions & {
  /**
   * Output format:
   * - "full": "12 hours", "1 day", "2 days"
   * - "abbreviated": "12h", "1d", "2d"
   * Default: "full"
   */
  format?: "full" | "abbreviated";
};

/**
 * Formats a duration value as minutes, hours, or days.
 * Converts to larger units using {@link toLargestUnit}.
 *
 * @param duration - The duration value to format
 * @param unit - The unit of the duration
 * @param options - Formatting options
 * @returns A formatted duration string
 *
 * @example
 * formatDurationLabel(1, "hour") // "1 hour"
 * formatDurationLabel(12, "hour") // "12 hours"
 * formatDurationLabel(24, "hour") // "1 day"
 * formatDurationLabel(48, "hour") // "2 days"
 * formatDurationLabel(25, "hour") // "25 hours"
 * formatDurationLabel(12, "hour", { format: "abbreviated" }) // "12h"
 * formatDurationLabel(24, "hour", { format: "abbreviated" }) // "1d"
 * formatDurationLabel(29, "hour", { format: "full", truncatePrecision: true }) // "1 day"
 * formatDurationLabel(30, "minute") // "30 minutes"
 * formatDurationLabel(60, "minute") // "1 hour"
 */
export function formatDurationLabel(
  duration: number,
  unit: DurationUnit,
  options: FormatDurationLabelOptions = {}
): string {
  const { format = "full", ...convertUnitOptions } = options;

  const output = convertUnit(duration, unit, convertUnitOptions);

  if (format === "abbreviated") {
    const shortUnit = getShortUnit(output.unit);
    return `${output.duration}${shortUnit}`;
  }

  return `${output.duration} ${pluralize(output.unit, output.duration)}`;
}

export type FormatDurationRangeLabelOptions = ConvertUnitOptions & {
  /**
   * Output format:
   * - "full": "Up to 2 days", "1 to 3 days", "30 minutes to 1 hour"
   * - "abbreviated": "<2d", "1-3d", "30m-1h"
   */
  format?: "full" | "abbreviated";
};

/**
 * Formats a range of duration values.
 * Converts each value independently and shows different units when they differ.
 * Zero is flexible and adapts to the unit of the other value.
 * When `from` is 0, uses "up to X" format.
 *
 * @param fromDuration - Start of range
 * @param toDuration - End of range (undefined for open-ended ranges like "72+")
 * @param unit - Unit for the input `from` and `to` values
 * @param options - Formatting options
 * @returns Formatted duration range string
 *
 * @example
 * formatDurationRangeLabel(0, undefined, "hour") // "0+ hours"
 * formatDurationRangeLabel(0, 48, "hour") // "Up to 2 days"
 * formatDurationRangeLabel(24, 72, "hour") // "1 to 3 days"
 * formatDurationRangeLabel(12, 48, "hour") // "12 hours to 2 days"
 * formatDurationRangeLabel(12, 36, "hour") // "12 to 36 hours"
 * formatDurationRangeLabel(72, undefined, "hour") // "3+ days"
 * formatDurationRangeLabel(30, 60, "minute") // "30 minutes to 1 hour"
 * formatDurationRangeLabel(0, 48, "hour", { format: "abbreviated" }) // "<2d"
 * formatDurationRangeLabel(24, 72, "hour", { format: "abbreviated" }) // "1-3d"
 * formatDurationRangeLabel(72, undefined, "hour", { format: "abbreviated" }) // "3d+"
 * formatDurationRangeLabel(30, 60, "minute", { format: "abbreviated" }) // "30m-1h"
 */
export function formatDurationRangeLabel(
  fromDuration: number,
  toDuration: number | undefined,
  unit: DurationUnit,
  options: FormatDurationRangeLabelOptions = {}
): string {
  const { format = "full", ...convertUnitOptions } = options;

  const to =
    toDuration === undefined ? undefined : convertUnit(toDuration, unit, convertUnitOptions);

  // When from is 0, adapt to the unit of 'to' for display consistency
  const from =
    fromDuration === 0 && to !== undefined
      ? { duration: 0, unit: to.unit }
      : convertUnit(fromDuration, unit, convertUnitOptions);

  const usesSameUnits = to === undefined || from.unit === to.unit;

  if (format === "abbreviated") {
    if (to === undefined) {
      return `${from.duration}${getShortUnit(from.unit)}+`;
    }
    if (from.duration === 0) {
      return `<${to.duration}${getShortUnit(to.unit)}`;
    }
    const fromLabel = usesSameUnits
      ? `${from.duration}`
      : `${from.duration}${getShortUnit(from.unit)}`;
    const toLabel = `${to.duration}${getShortUnit(to.unit)}`;
    return `${fromLabel}-${toLabel}`;
  }

  if (to === undefined) {
    return `${from.duration}+ ${pluralize(from.unit)}`;
  }

  const toLabel = pluralize(to.unit, to.duration, true);
  if (from.duration === 0) {
    return `Up to ${toLabel}`;
  }
  const fromLabel = usesSameUnits ? `${from.duration}` : pluralize(from.unit, from.duration, true);
  return `${fromLabel} to ${toLabel}`;
}

/**
 * Converts a duration to largest suitable unit: minutes -> hours -> days.
 *
 * Values large enough for the next unit are converted if they are fully divisible,
 * or if `truncatePrecision` is enabled. Multiple conversions may occur.
 *
 * @param duration - The duration value to convert
 * @param unit - The unit of the original duration
 * @param options - Conversion options
 * @returns An object with the converted duration and unit
 *
 * @example
 * // Values less than 1 of the next unit stay as-is.
 * convertUnit(45, "minute") // Stays 45 minutes
 *
 * // Values large enough for the next unit are converted if fully divisible.
 * convertUnit(60, "minute") // Converts to 1 hour
 * convertUnit(90, "minute") // Stays 90 minutes (not divisible by 60)
 * convertUnit(48, "hour")   // Converts to 2 days
 * convertUnit(36, "hour")   // Stays 36 hours (not divisible by 24)
 *
 * // With truncatePrecision, values always convert if large enough.
 * convertUnit( 60, "minute", { truncatePrecision: true }) // Converts to 1 hour
 * convertUnit( 90, "minute", { truncatePrecision: true }) // Converts to 1 hour (truncated precision)
 * convertUnit(119, "minute", { truncatePrecision: true }) // Converts to 1 hour (truncated precision)
 * convertUnit(120, "minute", { truncatePrecision: true }) // Converts to 2 hours
 * convertUnit( 45, "minute", { truncatePrecision: true }) // Stays 45 minutes (less than 60)
 *
 * // Units will convert multiple times, if possible.
 * convertUnit(1440, "minute") // 1 day
 * convertUnit(1500, "minute") // 25 hours (divisible by 60, not divisible by 24)
 * convertUnit(1500, "minute", { truncatePrecision: true }) // 1 day (truncated precision)
 * convertUnit(1501, "minute") // Stays 1501 minutes (not divisible by 60)
 * convertUnit(1501, "minute", { truncatePrecision: true }) // 1 day (truncated precision)
 *
 * // Days are the largest unit currently supported.
 * convertUnit(365, "day") // Stays 365 days
 * convertUnit(8760, "hour") // Converts to 365 days.
 *
 * // With keepOriginalUnit, no conversion is performed.
 * convertUnit(24, "hour", { keepOriginalUnit: true }) // Stays 24 hours
 * convertUnit(60, "minute", { keepOriginalUnit: true }) // Stays 60 minutes
 */
function convertUnit(
  duration: number,
  unit: DurationUnit,
  options: ConvertUnitOptions = {}
): { duration: number; unit: DurationUnit } {
  const { keepOriginalUnit = false, truncatePrecision = false } = options;

  if (keepOriginalUnit) {
    return { duration, unit };
  }

  if (unit === "minute") {
    const canConvertToHours = truncatePrecision
      ? duration >= 60
      : duration > 0 && duration % 60 === 0;
    if (canConvertToHours) {
      duration = Math.floor(duration / 60);
      unit = "hour";
    }
  }

  if (unit === "hour") {
    const canConvertToDays = truncatePrecision
      ? duration >= 24
      : duration > 0 && duration % 24 === 0;
    if (canConvertToDays) {
      duration = Math.floor(duration / 24);
      unit = "day";
    }
  }

  return { duration, unit };
}

/**
 * Convert a unit to the short form:
 * - 'minute' -> 'm'
 * - 'hour' -> 'h'
 * - 'day' -> 'd'
 */
function getShortUnit(unit: DurationUnit) {
  return unit.slice(0, 1);
}
